import React, { useState } from 'react';
import {
  callLinkBookingToLead,
  callUnlinkBookingFromLead,
  getLeads
} from '../../services/contentOpsService';

const SOURCE_COLORS = {
  calendly: 'bg-purple-600/20 text-purple-400',
  manual: 'bg-gray-600/20 text-gray-400'
};

const STATUS_COLORS = {
  confirmed: 'bg-green-600/20 text-green-400',
  cancelled: 'bg-red-600/20 text-red-400',
  tentative: 'bg-yellow-600/20 text-yellow-400'
};

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatTimeRange = (start, end) => {
  if (!start) return '—';
  const s = start.toDate ? start.toDate() : new Date(start);
  const e = end ? (end.toDate ? end.toDate() : new Date(end)) : null;

  const dateStr = s.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });

  const startTime = s.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });

  if (e) {
    const endTime = e.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return `${dateStr}, ${startTime} - ${endTime}`;
  }

  return `${dateStr}, ${startTime}`;
};

const BookingCard = ({ booking, onUpdate, leads = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [availableLeads, setAvailableLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const isUpcoming = booking.startTime &&
    (booking.startTime.toDate ? booking.startTime.toDate() : new Date(booking.startTime)) > new Date();

  const attendeeEmails = booking.attendees?.map(a => a.email).filter(Boolean) || [];
  const primaryAttendee = booking.attendees?.[0];

  const handleOpenLinkModal = async () => {
    setShowLinkModal(true);
    setLoadingLeads(true);
    try {
      const allLeads = await getLeads(null, 100);
      // Filter out already converted leads
      setAvailableLeads(allLeads.filter(l => l.status !== 'converted'));
    } catch (err) {
      console.error('Failed to load leads:', err);
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleLink = async () => {
    if (!selectedLeadId) return;
    setLinking(true);
    try {
      await callLinkBookingToLead(booking.id, selectedLeadId);
      setShowLinkModal(false);
      setSelectedLeadId('');
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to link booking:', err);
      alert('Failed to link: ' + (err.message || 'Unknown error'));
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm('Unlink this booking from the lead? The lead status will revert.')) return;
    setUnlinking(true);
    try {
      await callUnlinkBookingFromLead(booking.id);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to unlink booking:', err);
      alert('Failed to unlink: ' + (err.message || 'Unknown error'));
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <div className={`bg-gray-800 rounded-lg border ${isUpcoming ? 'border-green-600/30' : 'border-gray-700'} overflow-hidden`}>
      {/* Header - always visible */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-700/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-white font-medium truncate">{booking.summary}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full ${SOURCE_COLORS[booking.source] || SOURCE_COLORS.manual}`}>
                {booking.source}
              </span>
              {booking.isConversion && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-600/20 text-emerald-400">
                  Conversion
                </span>
              )}
              {isUpcoming && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-600/20 text-green-400">
                  Upcoming
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400 mt-1">
              {formatTimeRange(booking.startTime, booking.endTime)}
            </p>
            {primaryAttendee && (
              <p className="text-sm text-gray-500 mt-0.5">
                {primaryAttendee.displayName || primaryAttendee.email}
              </p>
            )}
          </div>
          <div className="text-gray-500 ml-2">
            {expanded ? '▼' : '▶'}
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-700 p-4 space-y-4">
          {/* Attendees */}
          {attendeeEmails.length > 0 && (
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Attendees</h4>
              <div className="space-y-1">
                {booking.attendees.map((a, i) => (
                  <div key={i} className="text-sm text-gray-300 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      a.responseStatus === 'accepted' ? 'bg-green-500' :
                      a.responseStatus === 'declined' ? 'bg-red-500' :
                      'bg-yellow-500'
                    }`} />
                    {a.displayName && <span>{a.displayName}</span>}
                    <span className="text-gray-500">{a.email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {booking.description && (
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Description</h4>
              <p className="text-sm text-gray-400 whitespace-pre-wrap max-h-32 overflow-y-auto">
                {booking.description}
              </p>
            </div>
          )}

          {/* Location */}
          {booking.location && (
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Location</h4>
              <p className="text-sm text-gray-400 break-all">
                {booking.location.startsWith('http') ? (
                  <a href={booking.location} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                    {booking.location}
                  </a>
                ) : booking.location}
              </p>
            </div>
          )}

          {/* Calendly Event Type */}
          {booking.calendlyEventType && (
            <div>
              <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Event Type</h4>
              <p className="text-sm text-purple-400">{booking.calendlyEventType}</p>
            </div>
          )}

          {/* Linked Lead */}
          <div>
            <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Linked Lead</h4>
            {booking.linkedLeadId ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-emerald-400">
                  Linked ({booking.linkedBy === 'auto' ? 'auto-matched' : 'manual'})
                </span>
                <button
                  onClick={handleUnlink}
                  disabled={unlinking}
                  className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                >
                  {unlinking ? 'Unlinking...' : 'Unlink'}
                </button>
              </div>
            ) : (
              <button
                onClick={handleOpenLinkModal}
                className="text-sm text-blue-400 hover:text-blue-300"
              >
                + Link to Lead
              </button>
            )}
          </div>

          {/* Meta */}
          <div className="text-xs text-gray-500 pt-2 border-t border-gray-700">
            Synced: {formatDate(booking.fetchedAt)}
          </div>
        </div>
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowLinkModal(false)}>
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-medium text-white mb-4">Link to Lead</h3>

            {loadingLeads ? (
              <p className="text-gray-400">Loading leads...</p>
            ) : (
              <>
                <select
                  value={selectedLeadId}
                  onChange={(e) => setSelectedLeadId(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white mb-4"
                >
                  <option value="">Select a lead...</option>
                  {availableLeads.map(lead => (
                    <option key={lead.id} value={lead.id}>
                      {lead.name || lead.widgetTitle} {lead.contactEmail ? `(${lead.contactEmail})` : ''}
                    </option>
                  ))}
                </select>

                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setShowLinkModal(false)}
                    className="px-4 py-2 text-gray-400 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLink}
                    disabled={!selectedLeadId || linking}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {linking ? 'Linking...' : 'Link'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCard;
